/* eslint-disable react-dom/no-unsafe-iframe-sandbox */
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, DatePicker, Form, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, Heading, Text } from "@sentricmusic/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { sanitizeInput } from "@/helpers/helpers";
import { useUIStateStore } from "@/store/uiStateStore";

import type { SpotifyTrack } from "../hooks/useSpotifyOperations";

import { useSpotifyOperations } from "../hooks/useSpotifyOperations";

// Recording type based on the API model
export type Recording = {
  title: string;
  performingartist: string;
  isrc?: string;
  recordlabel?: string;
  uicbarcode?: string; // UPC barcode
  releasedate?: string;
  spotifyidentifier?: string;
  spotifypopularity?: number;
  spotifypopularitylastupdatedutc?: string;
  recordingidentifier?: string;
  recordingamendmentidentifier?: string;
};

// Validation schema for recording form
const recordingFormSchema = z.object({
  title: z
    .string()
    .min(1, "Recording title is required")
    .max(200, "Recording title must be less than 200 characters")
    .refine((value) => {
      const sanitized = sanitizeInput(value);
      const safeSanitized = sanitized || "";
      return safeSanitized === value;
    }, "Recording title contains invalid characters"),

  performingartist: z
    .string()
    .min(1, "Performing artist is required")
    .max(200, "Performing artist name must be less than 200 characters"),

  isrc: z
    .string()
    .optional()
    .refine((value) => {
      if (value === undefined || value === null || value.trim() === "")
        return true;
      const isrcRegex = /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/;
      return isrcRegex.test(value);
    }, "ISRC must be in format: 2 letters, 3 alphanumeric characters, 7 digits (e.g., GBUM71505078)"),

  recordlabel: z
    .string()
    .max(200, "Record label must be less than 200 characters")
    .optional(),

  uicbarcode: z
    .string()
    .optional()
    .refine((value) => {
      if (value === undefined || value === null || value.trim() === "")
        return true;
      // UPC/EAN barcode validation (8, 12, 13, or 14 digits)
      const barcodeRegex = /^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/;
      return barcodeRegex.test(value);
    }, "UPC/Barcode must be 8, 12, 13, or 14 digits"),

  releasedate: z
    .string()
    .optional()
    .refine((value) => {
      if (value === undefined || value === null || value.trim() === "")
        return true;
      // Validate date format YYYY-MM-DD
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(value))
        return false;
      const date = new Date(value);
      return !Number.isNaN(date.getTime());
    }, "Release date must be in format YYYY-MM-DD"),
});

type RecordingFormData = z.infer<typeof recordingFormSchema>;

type CreateRecordingFormProps = {
  onRecordingCreated: (recording: Recording) => void;
  onClose: () => void;
  initialData?: Partial<Recording>;
  editingIndex?: number | null; // Add this to track if we're editing
};

export function CreateRecordingForm({
  onRecordingCreated,
  onClose,
  initialData,
  editingIndex,
}: CreateRecordingFormProps) {
  const { t } = useTranslation("songs");
  const flags = useFlags();
  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Determine if we're in edit mode
  const isEditing = editingIndex !== null && editingIndex !== undefined;

  // State for Spotify operations
  const [spotifyURL, setSpotifyURL] = useState("");
  const [spotifyResult, setSpotifyResult] = useState<SpotifyTrack | null>(null);
  const [spotifyLinkErrorMessage, setSpotifyLinkErrorMessage] = useState("");
  const [pending, setPending] = useState(false);

  // Spotify operations hook
  const {
    getSpotifyTrackByUrl,
  } = useSpotifyOperations({
    onError: (message) => {
      setSpotifyLinkErrorMessage(message);
    },
  });

  // Form setup
  const form = useForm<RecordingFormData>({
    resolver: zodResolver(recordingFormSchema),
    mode: "onChange",
    defaultValues: {
      title: initialData?.title ?? "",
      performingartist: initialData?.performingartist ?? "",
      isrc: initialData?.isrc ?? "",
      recordlabel: initialData?.recordlabel ?? "",
      releasedate: ((initialData?.releasedate) != null) ? initialData.releasedate.split("T")[0] : "",
    },
  });

  // Initialize Spotify result if editing and has Spotify data
  useEffect(() => {
    if (isEditing && initialData?.spotifyidentifier != null && initialData.spotifyidentifier.trim() !== "") {
      setSpotifyResult({
        id: initialData.spotifyidentifier,
        popularity: initialData.spotifypopularity ?? 0,
      } as SpotifyTrack);
    }
  }, [isEditing, initialData?.spotifyidentifier, initialData?.spotifypopularity]);

  // Handle Spotify URL submission
  const handleSpotifyButtonClick = async () => {
    if (!spotifyURL.trim()) {
      setSpotifyLinkErrorMessage(String(t("songs:link-spotify-track-invalid-url-error")));
      return;
    }

    setPending(true);
    setSpotifyLinkErrorMessage("");

    try {
      const track = await getSpotifyTrackByUrl(spotifyURL);
      if (track) {
        setSpotifyResult(track);

        // Auto-fill form with Spotify data
        const releaseDate = track.album?.release_date?.length === 4
          ? `${track.album.release_date}-01-01`
          : track.album?.release_date;

        form.setValue("title", sanitizeInput(track.name));
        form.setValue("performingartist", sanitizeInput(track.artists?.[0]?.name || ""));
        form.setValue("isrc", track.external_ids?.isrc ?? "");
        if ((releaseDate != null) && releaseDate.trim() !== "") {
          form.setValue("releasedate", releaseDate);
        }

        setSpotifyLinkErrorMessage("");
      }
    }
    catch (error) {
      console.error("Error fetching Spotify track:", error);
      setSpotifyLinkErrorMessage(String(t("songs:link-spotify-track-invalid-url-error")));
    }
    finally {
      setPending(false);
    }
  };

  // Remove Spotify data and clear form
  const removeSpotify = () => {
    setSpotifyResult(null);
    setSpotifyURL("");
    setSpotifyLinkErrorMessage("");
    form.reset({
      title: "",
      performingartist: "",
      isrc: "",
      recordlabel: "",
      releasedate: "",
    });
  };

  // Handle form submission
  const handleSubmit = (data: RecordingFormData) => {
    const recording: Recording = {
      title: data.title,
      performingartist: data.performingartist,
      isrc: (data.isrc != null) && data.isrc.trim() !== "" ? data.isrc : undefined,
      recordlabel: (data.recordlabel != null) && data.recordlabel.trim() !== "" ? data.recordlabel : undefined,
      uicbarcode: isEditing ? initialData?.uicbarcode : undefined, // Keep existing UIC barcode when editing
      releasedate: (data.releasedate != null) && data.releasedate.trim() !== "" ? `${data.releasedate}T00:00:00.000Z` : undefined,
      spotifyidentifier: spotifyResult?.id !== undefined && spotifyResult.id !== null ? String(spotifyResult.id) : (isEditing ? initialData?.spotifyidentifier : undefined),
      spotifypopularity: spotifyResult?.popularity ?? (isEditing ? initialData?.spotifypopularity : undefined),
      spotifypopularitylastupdatedutc: spotifyResult?.id !== undefined && spotifyResult.id !== null ? new Date().toISOString() : (isEditing ? initialData?.spotifypopularitylastupdatedutc : undefined),
    };

    onRecordingCreated(recording);
  };

  return (
    <div className={`space-y-6 ${isTunecore ? "text-white" : ""}`}>
      <div>
        <Heading as="h3" className="mb-2">
          {isEditing ? t("songs:edit-recording-header", "Edit Recording") : t("songs:add-recording-header")}
        </Heading>
        <Text className="text-sm opacity-80">
          {isEditing
            ? t("songs:edit-recording-description", "Update the recording details below.")
            : t("songs:recording-details-description", "Add details for this recording of your song.")}
        </Text>
      </div>

      {/* Spotify Import Section */}
      {Boolean(flags["pd-spotify-recording-song-details-feature"]) && (
        <div
          className={`flex flex-col gap-6 p-4 shadow-md ${isTunecore ? " text-white rounded-lg bg-tunecore-dark-element" : "bg-sentric-light-grey rounded-lg"}`}
        >
          {!spotifyResult ? (
            <>
              <Heading as="h4" className="text-lg">
                {t("songs:import-from-spotify-header")}
              </Heading>
              <Text className="text-sm">
                {isEditing
                  ? t("songs:edit-spotify-text", "You can update this recording with data from Spotify by pasting a track URL below.")
                  : t("songs:import-from-spotify-text")}
                {" "}
                <a
                  className="underline hover:scale-105"
                  href="https://support.spotify.com/us/artists/article/finding-your-artist-url/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("songs:link-spotify-instructions-link")}
                </a>
              </Text>
              <div className="flex flex-col space-y-4">
                <FormInput
                  type="text"
                  placeholder={String(t("songs:link-spotify-paste-track-url"))}
                  value={spotifyURL}
                  onChange={e => setSpotifyURL(e.target.value)}
                  hasError={!!spotifyLinkErrorMessage}
                />
                {spotifyLinkErrorMessage && (
                  <Text className="text-sm font-medium leading-snug text-red-700">{spotifyLinkErrorMessage}</Text>
                )}
                <div className="self-end">
                  <Button
                    type="button"
                    onClick={() => void handleSpotifyButtonClick()}
                    disabled={pending}
                    label={t("songs:link-spotify-fetch-track")}

                    className={`self-end group ${isTunecore
                      ? "text-white font-tunecore hover:text-tunecore-green"
                      : "bg-[#1ED760] hover:bg-[#1DB954] text-black"}`}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <iframe
                title="Spotify Preview"
                src={`https://open.spotify.com/embed/track/${spotifyResult.id}`}
                allow="encrypted-media"
                className="w-full"
                height="80"
                sandbox="allow-scripts allow-same-origin"
                style={{ borderRadius: "8px" }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={removeSpotify}
                label={t("songs:remove-selection")}
              />
            </div>
          )}
        </div>
      )}

      {/* Recording Details Form */}
      <Form {...form}>
        <form onSubmit={e => void form.handleSubmit(handleSubmit)(e)} className="space-y-6">
          <div className="space-y-4">

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("songs:title-placeholder")}</FormLabel>
                  <FormControl>
                    <FormInput
                      {...field}
                      placeholder={String(t("songs:title-placeholder"))}
                      className="form-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="performingartist"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("songs:performed-by")}</FormLabel>
                  <FormControl>
                    <FormInput
                      {...field}
                      placeholder={String(t("songs:performed-by"))}
                      className="form-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isrc"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("songs:isrc-placeholder")}
                    {" "}
                    (Optional)
                  </FormLabel>
                  <FormControl>
                    <FormInput
                      {...field}
                      placeholder={String(t("songs:isrc-placeholder"))}
                      className="form-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="recordlabel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("songs:record-label-placeholder")}
                    {" "}
                    (Optional)
                  </FormLabel>
                  <FormControl>
                    <FormInput
                      {...field}
                      placeholder={String(t("songs:record-label-placeholder"))}
                      className="form-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="releasedate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("songs:release-date")}
                    {" "}
                    (Optional)
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      placeholder="DD/MM/YYYY"
                      value={field.value != null && field.value.trim() !== "" ? new Date(field.value) : undefined}
                      onChange={(date: Date | undefined) => {
                        if (date) {
                          // Format as YYYY-MM-DD in local timezone
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, "0");
                          const day = String(date.getDate()).padStart(2, "0");
                          const formattedDate = `${year}-${month}-${day}`;
                          field.onChange(formattedDate);
                        }
                        else {
                          field.onChange("");
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end pt-4 space-x-4">
            <Button
              type="button"
              onClick={onClose}
              label={t("songs:cancel-and-close")}
              variant={isTunecore ? "secondary" : "darkSecondary"}
              className={isTunecore ? "border-tunecore-light-grey text-white hover:bg-tunecore-light-grey" : ""}
            />
            <Button
              type="submit"
              disabled={pending}
              label={isEditing ? t("songs:update-recording-button", "Update Recording") : t("songs:add-recording-button")}
            />
          </div>
        </form>
      </Form>
    </div>
  );
}
